import { useEffect, useState } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'
import type { WhatsAppMessage } from '@/lib/services/billing'
import { getMessagesForParty } from '@/lib/services/billing'

interface WhatsAppHistoryBadgePartyProps {
  partyId: string
  unitId: string
}

/**
 * Badge showing WhatsApp message history for a contact (party).
 * Displays aggregated counts of charges and alerts sent to this contact, with tooltip showing last send time.
 */
export function WhatsAppHistoryBadgeParty({ partyId, unitId }: WhatsAppHistoryBadgePartyProps) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<WhatsAppMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        console.log('[WhatsAppHistoryBadgeParty] Loading messages for partyId:', partyId, 'unitId:', unitId)
        const msgs = await getMessagesForParty(partyId, unitId)
        console.log('[WhatsAppHistoryBadgeParty] Messages loaded:', msgs)
        setMessages(msgs)
        setError(null)
      } catch (err) {
        console.error('[WhatsAppHistoryBadgeParty] Failed to load WhatsApp messages:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [partyId, unitId])

  if (loading) return null

  const charges = messages.filter(m => m.type === 'charge')
  const alerts = messages.filter(m => m.type === 'duedate_alert')

  if (messages.length === 0) return null

  const lastChargeTime = charges.length > 0 ? new Date(charges[0].sent_at).toLocaleString('pt-BR') : null
  const lastAlertTime = alerts.length > 0 ? new Date(alerts[0].sent_at).toLocaleString('pt-BR') : null

  const tc = (key: string, params?: Record<string, string>) =>
    t(`billing.contacts.whatsappNotifications.${key}`, params)

  return (
    <div className="flex gap-2 flex-wrap items-center">
      {charges.length > 0 && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
          title={tc('chargeTooltip', { time: lastChargeTime || '' })}
        >
          💬 {tc('chargeCount', { count: String(charges.length) })}
        </span>
      )}
      {alerts.length > 0 && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          title={tc('alertTooltip', { time: lastAlertTime || '' })}
        >
          🔔 {tc('alertCount', { count: String(alerts.length) })}
        </span>
      )}
      {error && (
        <span className="text-xs text-red-500 dark:text-red-400" title={error}>
          ⚠️ Error loading messages
        </span>
      )}
    </div>
  )
}
